<template>
    <div class="p-4">
        <ReplaceLayoutModal
            :file="replaceLayoutFile"
            @layout-replaced="loadFiles"
            @close="replaceLayoutFile = null"></ReplaceLayoutModal>
        <b-modal id="remove-file-modal" centered size="lg"
                 :title="'Remove file'"
                 @ok="onRemoveFile">
            <p class="my-4" v-if="selectedFile !== null && selectedFile.is_folder">
                Are you sure you want to delete this folder?
                It will also remove all the files in the folder.
            </p>
            <p class="my-4" v-else>Are you sure you want to delete this file?</p>
        </b-modal>

        <b-modal
            id="folderModal"
            ref="folderModal"
            title="Create new folder"
            size="lg"
            button-size="lg"
            ok-title="Create"
            @show="folderName = null"
            @ok="handleFolderModalOk"
        >
            <form ref="folderForm" @submit.stop.prevent="handleFolderModalOk">
                <b-form-group
                    label="Folder name"
                    label-for="name-input"
                    invalid-feedback="Invalid folder name"
                    :state="folderModalState">
                    <b-form-input
                        id="name-input"
                        v-model="folderName"
                        :state="folderModalState"
                        required>
                    </b-form-input>
                </b-form-group>
            </form>
        </b-modal>


        <div class="menu-bar clearfix" :class="{ 'no-border': !hasManagerPermission('files.edit') }">
            <div class="pull-left upload-button-container"
                 v-if="hasManagerPermission('files.edit')">
                <b-button @click="triggerUploadSelection"
                          variant="primary">
                    <b-icon icon="upload"></b-icon>
                    Upload file
                </b-button>
                <em>PDF and image (jpeg or png) files only.</em>
            </div>

            <div class="dropdown pull-right">
                <button class="menu-icon-link btn btn-primary"
                        href="javascript:void(0)"
                        v-b-modal.folderModal
                        v-b-tooltip.hover
                        v-if="currentFolder === null && hasManagerPermission('files.edit')"
                        @click="folderName = null; folderModalState = null; "
                        title="Create folder">
                    <b-icon icon="folder"></b-icon>
                </button>

                <button class="menu-icon-link btn btn-primary"
                        href="javascript:void(0)"
                        v-if="hasManagerPermission('files.delete')"
                        v-b-modal.remove-file-modal
                        v-b-tooltip.hover
                        title="Delete selected">
                    <b-icon icon="trash"></b-icon>
                </button>

                <button class="menu-icon-link btn btn-primary"
                        href="javascript:void(0)"
                        @click="viewFile"
                        v-b-tooltip.hover
                        title="Preview selected">
                    <b-icon icon="eye"></b-icon>
                </button>
            </div>
        </div>

        <div class="row">
            <div class="col-md-8 file-list">

                <b-breadcrumb v-if="project">
                    <b-breadcrumb-item @click="currentFolder = null; selectedFolder = null">
                        <b-icon icon="house-fill"></b-icon>
                        {{ project.name }}
                    </b-breadcrumb-item>

                    <b-breadcrumb-item v-if="currentFolder">{{ currentFolder.name }}</b-breadcrumb-item>
                </b-breadcrumb>

                <b-form>
                    <b-list-group v-if="files.length">
                        <template v-for="file in files">

                            <FileListItem
                                :key="file.id"
                                @showReplaceLayoutModal="showReplaceLayoutModal"
                                @toggleSelected="toggleSelected"
                                @toggleCurrent="toggleCurrent"
                                @showDetails="showDetails"
                                :file="file" :currentFolder="currentFolder" :selectedFile="selectedFile"/>

                            <div v-if="currentFolder !== null && currentFolder.id === file.id">
                                <template v-for="child in file.children">
                                    <FileListItem
                                        :key="child.id"
                                        @showReplaceLayoutModal="showReplaceLayoutModal"
                                        @toggleSelected="toggleSelected"
                                        @toggleCurrent="toggleCurrent"
                                        @showDetails="showDetails"
                                        :file="child" :current-folder="currentFolder" :selected-file="selectedFile">
                                    </FileListItem>
                                </template>
                            </div>
                        </template>
                    </b-list-group>

                    <b-form-group hidden label="Upload files" label-for="projects-index-file">
                        <b-form-file id="projects-index-file"
                                     v-model="filesList"
                                     @input="onFilesChanged"
                                     accept="application/pdf,image/jpeg,image/png"
                                     placeholder="Choose files or drop them here..."
                                     drop-placeholder="Drop files here..."
                                     multiple></b-form-file>
                    </b-form-group>

                    <template v-if="uploads.length">
                        <b-list-group-item v-if="!file.hidden"
                                           v-for="(file, index) in uploads"
                                           :key="index">
                            <div class="row mb-1">
                                <div class="col-sm-7" v-text="file.name"></div>
                                <div class="col-sm-5 pt-1">
                                    <b-progress :value="file.progress >= 0 ? file.progress : 100"
                                                :variant="fileUploadVariant(file)"></b-progress>
                                </div>
                            </div>
                        </b-list-group-item>
                    </template>

                </b-form>
            </div>
        </div>

        <FileDetailsModal v-if="detailsFile" :file="detailsFile" :key="detailsFile.id" @updated="loadFiles"/>
    </div>
</template>

<script>
import axios from 'axios';
import {each} from 'lodash';
import ReplaceLayoutModal from "./parts/ReplaceLayoutModal.vue";
import FileDetailsModal from "./parts/FileDetailsModal.vue";
import FileListItem from "@components/files/parts/FileListItem.vue";

export default {
    components: {
        FileListItem,
        FileDetailsModal,
        ReplaceLayoutModal
    },
    data() {
        return {
            files: [],
            uploads: [],
            dataUris: {},
            fileUploadPromise: null,
            activeFile: null,
            filesList: [],
            project: null,

            folderName: null,
            folderModalState: null,

            currentFolder: null,
            selectedFile: null,
            detailsFile: null,
            replaceLayoutFile: null,

            role: null,
            allowedFileExtensions: ['pdf', 'jpg', 'jpeg', 'png'],

            loadingTask: null,
            filesAreLoading: false,
            fileLoadingInterval: null
        }
    },
    watch: {
        'project.id': function () {
            this.role = this.$store.state.project.user.role;
            this.loadFiles();
        }
    },
    mounted() {
        console.log(this.hasManagerPermission('projects.edit'));


        this.project = this.$store.state.project;

        // call loadFiles every 20 seconds
        const instance = this;
        this.fileLoadingInterval = setInterval(() => {
            instance.loadFiles();
        }, 20000);

    },
    destroyed() {
        if (this.fileLoadingInterval) {
            clearInterval(this.fileLoadingInterval);
        }
    },
    methods: {
        loadFiles() {
            if (!this.$store.state.project.id) {
                return;
            }

            if (this.filesAreLoading) {
                // cancel the previous request
                this.loadingTask.abort();
            }

            // create a new cancel token
            this.loadingTask = new AbortController();
            this.filesAreLoading = true;

            let token = this.$store.state.token;
            axios({
                signal: this.loadingTask.signal,
                url: this.$store.getters.apiBase + '/projects/' + this.$route.params.id + '/files',
                method: 'GET',
                params: {
                    is_public: true
                },
                headers: {"Authorization": "Bearer " + token}
            })
                .then(response => {
                    this.files = response.data.data;
                    this.filesAreLoading = false;
                })
                .catch(error => {
                    console.log(error);
                    this.filesAreLoading = false;
                });
        },

        fileUploadVariant: function (file) {
            if (file.progress == -1) {
                return 'danger';
            } else if (file.progress >= 100) {
                return 'success';
            }
            return 'primary';
        },

        readAndSendFile(file, index) {
            let that = this;
            return new Promise((resolve, reject) => {
                let reader = new FileReader();

                file.progress = 0;

                reader.addEventListener('load', () => {


                    const fileName = file.name.substring(0, file.name.lastIndexOf('.'));
                    const fileExtension = this.getExtension(file.name).toLowerCase();
                    if (!that.allowedFileExtensions.includes(fileExtension)) {
                        const message = 'Allowed file extensions are: ' + this.allowedFileExtensions.join(', ');
                        this.$bvModal.msgBoxOk(message, {
                            title: 'Invalid file',
                            okVariant: 'danger',
                            size: 'sm',
                            centered: true
                        }).then(() => {
                            this.loadFiles();
                        });
                        reject(message);
                    } else {
                        resolve(this.sendFile((this.removeSpecialChars(fileName) + '.' + fileExtension), new Blob([reader.result], {type: file.type}), index));
                    }
                }, false);

                reader.addEventListener('error', (error) => {
                    reject(error);
                }, false);

                reader.readAsArrayBuffer(file);
            });
        },
        getExtension(filename) {
            return filename.split(".").pop();
        },
        removeSpecialChars(str) {
            return str.replace(/(?!\w|\s)./g, '')
                .replace(/\s+/g, ' ')
                .replace(/^(\s*)([\W\w]*)(\b\s*$)/g, '$2');
        },

        onRemoveFile() {
            if (this.selectedFile === null) {
                return;
            }
            let token = this.$store.state.token;


            this.selectedFile.hidden = true;

            let url = this.$store.getters.apiBase + '/projects/' + this.$route.params.id;

            if (this.selectedFile.is_folder) {
                url += '/folders/';
            } else {
                url += '/files/';
            }

            url += this.selectedFile.id;

            return axios({
                url: url,
                method: 'DELETE',
                headers: {"Authorization": "Bearer " + token}
            })
                .then(response => {
                    this.loadFiles();
                })
                .catch(error => {
                    this.selectedFile.hidden = false;
                });
        },

        onFilesChanged(files) {
            let promise = this.fileUploadPromise || Promise.resolve(true);

            each(files, (file) => {
                let index = this.uploads.push(file) - 1;

                promise = promise.finally(() => {
                    return this.readAndSendFile(file, index);
                });
            });

            this.filesList = [];
            this.fileUploadPromise = promise;
        },

        sendFile(name, blobObject, index) {
            let token = this.$store.state.token;

            let formData = new FormData();
            formData.set('file[name]', name);
            formData.set('main_file', blobObject, name);

            if (this.currentFolder) {
                formData.set('parent_id', this.currentFolder ? this.currentFolder.id : null);
            }

            return axios({
                url: this.$store.getters.apiBase + '/projects/' + this.$route.params.id + '/files',
                method: 'POST',
                headers: {"Authorization": "Bearer " + token},
                data: formData,
                onUploadProgress: (e) => {
                    this.uploads[index].progress = Math.round(e.loaded / e.total * 100);
                    this.$set(this.uploads, index, this.uploads[index]);
                }
            })
                .then(response => {
                    this.uploads[index].progress = 100;
                    this.$set(this.uploads, index, this.uploads[index]);

                    setTimeout(() => {
                        this.uploads[index].hidden = true;
                        this.$set(this.uploads, index, this.uploads[index]);

                        this.loadFiles();
                    }, 2000);
                })
                .catch(error => {
                    this.uploads[index].progress = -1;
                    this.$set(this.uploads, index, this.uploads[index]);
                });
        },

        checkFolderFormValidity() {
            const valid = this.$refs.folderForm.checkValidity()
            this.folderModalState = valid;
            return valid;
        },
        handleFolderModalOk(bvModalEvt) {
            // Prevent modal from closing
            bvModalEvt.preventDefault()
            // Trigger submit handler
            this.createFolder()
        },
        resetFolderModal() {
            this.folderName = '';
            this.folderModalState = null;
        },

        createFolder() {

            // Exit when the form isn't valid
            if (!this.checkFolderFormValidity()) {
                return
            }

            let token = this.$store.state.token;

            let formData = new FormData();
            formData.set('name', this.folderName);

            axios({
                url: this.$store.getters.apiBase + '/projects/' + this.$route.params.id + '/folders',
                method: 'POST',
                headers: {"Authorization": "Bearer " + token},
                data: formData,
            })
                .then(() => {

                    this.$nextTick(() => {
                        this.$bvModal.hide('folderModal');
                    });

                    this.loadFiles();
                })
                .catch(error => {
                    console.log(error, 'error');
                });
        },
        toggleSelected($event, file) {
            $event.preventDefault();
            if (this.selectedFile !== null && this.selectedFile.id === file.id) {
                this.selectedFile = null;
            } else {
                this.selectedFile = file;
            }
        },

        toggleCurrent($event, file) {
            if ($event) {
                $event.preventDefault();
            }
            this.selectedFile = null;

            if (file.is_folder) {
                if (this.currentFolder !== null && this.currentFolder.id === file.id) {
                    this.currentFolder = null;
                } else {
                    this.currentFolder = file;
                }

            } else {
                this.selectedFile = file;
                this.viewFile();
            }
        },
        triggerUploadSelection() {
            document.getElementById('projects-index-file').click();
        },
        viewFile() {
            if (this.selectedFile === null) {
                return;
            }

            if (this.selectedFile.is_folder) {
                this.toggleCurrent(false, this.selectedFile);
            } else {
                let win = window.open(this.selectedFile.file, '_blank');
                win.focus();
            }
        },

        showDetails(file) {
            this.detailsFile = file;
            this.$nextTick(() => this.$bvModal.show('file-details-modal'));
        },

        showReplaceLayoutModal(file) {
            this.replaceLayoutFile = file;
            this.$nextTick(() => this.$bvModal.show('replace-layout-modal'));
        }
    }
}
</script>

<style scoped>


.list-group .folderContainer ~ .fileContainer {
    margin-top: 20px;
}

.list-group :not(.folderContainer) + .fileContainer {

    margin-top: 0;
}

.file-actions {

    .btn {
        border-radius: 3px;
        line-height: normal;
    }
}

.no-border {
    border: none;
}

</style>
